import * as Pipeline from '../core/utils/pipeline';
import {
  AdsAlertErrors,
  AlertAnimationDirection,
  AlertAnimationDirections,
  AlertLevel,
  AlertLevels,
  VisibleChangeEvent,
  VisibleChangeHandler,
} from './ads-alert.types';
import { AlertCloseButton } from './components/ads-alert-close-button/ads-alert-close-button';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Component, Element, Event, EventEmitter, Host, Method, Prop, State, VNode, Watch, h } from '@stencil/core';
import { Dictionary } from '../core/classes/Dictionary';
import { delay, existValueInEnum, isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/index';
import { generateId, showErrorMessage } from '../core/utils/components';

/**
 * Alerts provide contextual application-level messaging. Alerts classes indicate the state of the application and provide contextual information about what the user should do.
 * Alert text should be as short as possible, can include links and can be dismissed. There are 4 alert classes:
 * "Default" is used for informational messages where the user does not need to take immediate action.
 * "Success" is used to indicate the successful completion of a user action.
 * "Warning" is used to indicate a problem occurred completing the user action where further action may be necessary.
 * "Danger" is used to indicate failures where the user action could not be completed.
 * @tag `<ads-alert>`
 * @slot - Content rendered as rich text inside the component
 * @example
 * <ads-alert class=is-default>This alert indicates the default style</ads-alert>
 * <ads-alert class=is-success>This alert indicates success</ads-alert>
 * <ads-alert class=is-warning>This alert indicates warnings</ads-alert>
 * <ads-alert class=is-danger>This alert indicates failures</ads-alert>
 */
@Component({
  tag: 'ads-alert',
  styleUrl: 'ads-alert.scss',
  shadow: true,
})
export class AdsAlert implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-alert'));
    this.component = 'ads-alert';
    Dictionary.registerComponent(this);
  }

  @Element() private el: HTMLAdsAlertElement;

  /**
   * @hidden
   */
  @State() isVisibleInternal = true;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The animation direction (optional)
   */
  @Prop({ reflect: true }) animation?: AlertAnimationDirection;

  /**
   * The animation delay in milliseconds (optional)
   * @default 0
   */
  @Prop({ reflect: true }) animationDelay = 0;

  /**
   * Whether or not the alert is visible (optional)
   */
  @Prop({ reflect: true }) visible?: boolean;

  /**
   * Whether or not the alert has a close button (optional)
   * @default true
   */
  @Prop({ reflect: true }) hasCloseButton = true;

  /**
   * Whether the alert should notify the user (screen reader) immediately and interrupt on change, or wait until the screen reader is free (optional)
   * @default false
   */
  @Prop({ reflect: true }) isImportant = false;

  /**
   * The alert level (optional)
   */
  @Prop({ reflect: true }) level?: AlertLevel;

  /**
   * The alert content when not provided via a slot (optional)
   */
  @Prop({ reflect: true }) text?: string;

  /**
   * Callback function for visibleChange events (optional)
   */
  @Prop() visibleChangeHandler?: VisibleChangeHandler;

  /**
   * Emits the 'visibleChange' custom event, when an attempt to change the visibility is made
   * Usage: `document.querySelector('ads-alert').addEventListener('visibleChange', function(evt) {});`
   * @event
   */
  @Event({ bubbles: false }) visibleChange: EventEmitter<VisibleChangeEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-alert'));
  }

  /**
   * Handle visible property changes
   * @param {boolean} newValue The new value for "visible"
   * @hidden
   */
  @Watch('visible')
  handleVisibleChange(newValue: boolean): void {
    void this.handleVisibility(newValue);
  }

  /**
   * Called when animation prop changes
   * @param {AlertAnimationDirection} newValue new value
   * @hidden
   */
  @Watch('animation')
  onAnimationChanged(newValue: AlertAnimationDirection): void {
    if (newValue && !Object.values(AlertAnimationDirections).toString().includes(newValue)) {
      showErrorMessage(AdsAlertErrors.INVALID_ANIMATION_DIRECTION, this.el);
    }
  }

  /**
   * Called when level prop changes
   * @param {AlertLevel} newValue new value
   * @hidden
   */
  @Watch('level')
  onLevelChanged(newValue: AlertLevel): void {
    if (newValue && !Object.values(AlertLevels).toString().includes(newValue)) {
      showErrorMessage(AdsAlertErrors.INVALID_ALERT_LEVEL, this.el);
    }
  }

  /**
   * Called when animationDelay prop changes
   * @param {number} newValue new value
   * @hidden
   */
  @Watch('animationDelay')
  onAnimationDelayChanged(newValue: number): void {
    // wait for next frame in case this.animation is also going to update
    setTimeout(() => {
      if (newValue && !this.animation) {
        showErrorMessage(AdsAlertErrors.DELAY_SET_WITHOUT_ANIMATION, this.el);
      }
    }, 50);
  }

  /**
   * Opens the alert box
   * @return {Promise<void>} Opens the alert
   */
  @Method()
  open(): Promise<void> {
    return !this.isVisibleInternal ? this.handleVisibility(true) : null;
  }

  /**
   * Closes the alert box
   * @return {Promise<void>} Closes the alert
   */
  @Method()
  close(): Promise<void> {
    return this.isVisibleInternal ? this.handleVisibility(false) : null;
  }

  /**
   * Returns whether the alert is currently visible or not
   * @return {Promise<boolean>} Whether the alert is visible
   */
  @Method()
  value(): Promise<boolean> {
    return Promise.resolve(this.isVisibleInternal);
  }

  /**
   * Handles resolving the visibility change of the alert
   * @param {boolean}         isVisible   True if visible. False otherwise
   * @return {Promise<void>}              Visibility promise
   * @hidden
   */
  private async handleVisibility(isVisible: boolean): Promise<void> {
    // Emit the event without updating anything.
    if (this.visible !== undefined && this.visible !== null && this.visible !== isVisible) {
      this.visibleChange.emit({ el: this.el, visible: isVisible });

      if (!isNullOrUndefined(this.visibleChangeHandler)) {
        this.visibleChangeHandler({ el: this.el, visible: isVisible });
      }

      return;
    }

    return new Promise((resolve) => {
      const alert = this.el.shadowRoot.querySelector('.c-alert');

      if (isVisible) {
        // Delay opening the box
        delay(!isNullOrUndefinedOrEmpty(this.animation) && this.animationDelay > 0, Math.abs(this.animationDelay))
          .then(() => {
            alert.classList.remove('is-hidden');
            alert.classList.remove('is-closed');

            // Delay sending the emit until the box disappears (500ms set in css)
            delay(!isNullOrUndefinedOrEmpty(this.animation), 500)
              .then(() => {
                this.isVisibleInternal = isVisible;

                if (this.visible !== this.isVisibleInternal) {
                  this.visibleChange.emit({ el: this.el, visible: true });
                  !isNullOrUndefined(this.visibleChangeHandler) ? this.visibleChangeHandler({ el: this.el, visible: true }) : null;
                }

                return resolve();
              })
              .catch(() => {
                return;
              });
          })
          .catch(() => {
            return;
          });
      } else if (isVisible === false) {
        alert.classList.add('is-closed');
        // Delay sending the emit until the box disappears (500ms set in css)
        delay(!isNullOrUndefinedOrEmpty(this.animation), 500)
          .then(() => {
            alert.classList.add('is-hidden');
            this.isVisibleInternal = isVisible;

            if (this.visible !== this.isVisibleInternal) {
              this.visibleChange.emit({ el: this.el, visible: false });
              !isNullOrUndefined(this.visibleChangeHandler) ? this.visibleChangeHandler({ el: this.el, visible: false }) : null;
            }

            return resolve();
          })
          .catch(() => {
            return;
          });
      }
    });
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
    this.visible !== undefined && this.visible !== null && !this.visible ? void this.handleVisibility(false) : null;

    if (this.animation && !Object.values(AlertAnimationDirections).toString().includes(this.animation)) {
      showErrorMessage(AdsAlertErrors.INVALID_ANIMATION_DIRECTION, this.el);
    }

    if (this.level && !Object.values(AlertLevels).toString().includes(this.level)) {
      showErrorMessage(AdsAlertErrors.INVALID_ALERT_LEVEL, this.el);
    }

    if (this.animationDelay && isNullOrUndefinedOrEmpty(this.animation)) {
      showErrorMessage(AdsAlertErrors.DELAY_SET_WITHOUT_ANIMATION, this.el);
    }
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    // Generate the class list
    const classes = ['c-alert'];
    const isDialog = this.hasCloseButton;
    const hasAnimation = this.animation && existValueInEnum(AlertAnimationDirections, this.animation);
    if (hasAnimation) {
      classes.push(`is-animated is-animated-${this.animation}`);
    }

    // Generate the close button, if required
    const label = Dictionary.get('Dismiss message', 'DISMISS_MESSAGE');
    const closeButton = this.hasCloseButton ? ((<AlertCloseButton label={label} clickHandler={() => this.close()} />) as HTMLElement) : null;

    const attributes = {
      role: isDialog ? 'alertdialog' : 'alert',
      'aria-describedby': isDialog ? `ads:alert:${this.identifier}` : null,
      'aria-live': this.isImportant ? 'assertive' : 'polite',
      'aria-label': isDialog ? `${this.level || 'information'} alert: ${this.text || 'Alert message'}` : null,
    };

    const hostAttributes = {
      'data-id': this.identifier,
      class: !isNullOrUndefinedOrEmpty(this.level) ? `is-${this.level}` : null,
    };

    return (
      <Host {...hostAttributes}>
        <div class={classes.join(' ')} {...attributes}>
          <div class="c-alert__container">
            <div class="c-alert__text" id={`ads:alert:${this.identifier}`}>
              {!isNullOrUndefinedOrEmpty(this.text) ? this.text : <slot />}
            </div>
            {closeButton}
          </div>
        </div>
      </Host>
    ) as VNode;
  }
}
